import React, { useState } from 'react';
import { X, Upload, FileText, Percent, DollarSign } from 'lucide-react';
import { api } from '../../services/apiService';
import { toastService } from '../../services/toastService';

import { RubyGemMark } from '../RubyGemMark';
interface Props {
  tenantId: string;
  locationId: string;
  onClose: () => void;
  onUploaded: () => void;
}

const MAX_BYTES = 10 * 1024 * 1024;

const AgreementUploadModal: React.FC<Props> = ({ tenantId, locationId, onClose, onUploaded }) => {
  const [file, setFile] = useState<File | null>(null);
  const [agreementName, setAgreementName] = useState('');
  const [maxRate, setMaxRate] = useState<string>('');
  // Max flat fee is captured as a string of digits (cents). Display = (cents / 100).toFixed(2).
  // Typing "1" → "0.01", "10" → "0.10", "100" → "1.00".
  const [maxFlatCents, setMaxFlatCents] = useState<string>('');
  const [version, setVersion] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [notes, setNotes] = useState('');
  const [setActive, setSetActive] = useState(true);
  const [uploading, setUploading] = useState(false);

  const maxFlatDollars = parseInt(maxFlatCents || '0', 10) / 100;
  const maxFlatDisplay = maxFlatCents === '' ? '' : maxFlatDollars.toFixed(2);

  const handleMaxFlatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '');
    // Cap at 8 digits → up to $999,999.99
    setMaxFlatCents(digits.slice(0, 8));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== 'application/pdf') {
      toastService.error('Only PDF files allowed');
      return;
    }
    if (f.size > MAX_BYTES) {
      toastService.error(`File too large (max ${MAX_BYTES / 1024 / 1024} MB)`);
      return;
    }
    setFile(f);
    if (!agreementName) {
      // Pre-seed from file name minus extension
      setAgreementName(f.name.replace(/\.pdf$/i, ''));
    }
  };

  const validate = (): string | null => {
    if (!file) return 'Choose a PDF file';
    if (!agreementName.trim()) return 'Agreement name is required';
    const r = Number(maxRate);
    if (isNaN(r) || r < 0 || r > 100) return 'Max rate must be 0–100%';
    if (maxFlatDollars < 0) return 'Max flat fee must be ≥ 0';
    return null;
  };

  const handleUpload = async () => {
    const err = validate();
    if (err) { toastService.error(err); return; }
    if (!file) return;
    try {
      setUploading(true);
      const presign = await api.posNmi.presignAgreementUpload(tenantId, locationId, {
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      });
      const putRes = await fetch(presign.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type, 'x-amz-server-side-encryption': 'AES256' },
        body: file,
      });
      if (!putRes.ok) {
        toastService.error('Upload to S3 failed');
        return;
      }
      await api.posNmi.confirmAgreementUpload(tenantId, locationId, {
        agreementId: presign.agreementId,
        s3Key: presign.s3Key,
        fileName: file.name,
        agreementName: agreementName.trim(),
        maxRate: Number(maxRate),
        maxFlat: maxFlatDollars,
        contentType: file.type,
        sizeBytes: file.size,
        effectiveDate: effectiveDate || undefined,
        version: version || undefined,
        notes: notes || undefined,
        setActive,
      });
      toastService.success(`Uploaded "${agreementName.trim()}"`);
      onUploaded();
      onClose();
    } catch (err: any) {
      toastService.error(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 my-8 p-5 max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-stone-800">Create Surcharge Agreement</h3>
            <p className="text-xs text-stone-500 mt-0.5">PDF agreement + the maximum surcharge it negotiates</p>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600"><X size={18} /></button>
        </div>

        {/* PDF picker */}
        <label className="block">
          <p className="text-[11px] font-medium text-stone-700 mb-1">Agreement PDF *</p>
          <div className={`flex items-center gap-3 p-4 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
            file ? 'border-emerald-300 bg-emerald-50' : 'border-stone-300 bg-stone-50 hover:bg-stone-100'
          }`}>
            <div className="w-10 h-10 rounded-lg bg-white border border-stone-200 flex items-center justify-center">
              {file ? <FileText size={18} className="text-emerald-600" /> : <Upload size={18} className="text-stone-400" />}
            </div>
            <div className="flex-1 min-w-0">
              {file ? (
                <>
                  <p className="text-sm font-medium text-emerald-800 truncate">{file.name}</p>
                  <p className="text-[11px] text-emerald-600">{(file.size / 1024).toFixed(0)} KB · click to replace</p>
                </>
              ) : (
                <p className="text-sm text-stone-600">Click to choose PDF (max 10 MB)</p>
              )}
            </div>
            <input type="file" accept="application/pdf" className="hidden" onChange={handleFileChange} />
          </div>
        </label>

        {/* Agreement name */}
        <div className="mt-4">
          <label className="block text-[11px] font-medium text-stone-700 mb-1">Agreement Name *</label>
          <input
            type="text"
            value={agreementName}
            onChange={(e) => setAgreementName(e.target.value)}
            placeholder="Visa Surcharge Disclosure"
            className="w-full px-3 py-2 text-sm text-stone-800 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
          />
          <p className="text-[10px] text-stone-500 mt-1">Shown in the active-agreement dropdown</p>
        </div>

        {/* Per-agreement caps — the bound */}
        <div className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200">
          <p className="text-[11px] font-semibold text-amber-900 mb-1">Maximum Surcharge Negotiated by This Agreement *</p>
          <p className="text-[10px] text-amber-800/80 mb-3">When you set this agreement active, these become the runtime ceilings. Tenant cannot exceed them.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-medium text-stone-700 mb-1">Max Rate (%)</label>
              <div className="relative">
                <input
                  type="number" step="0.01" min="0" max="100"
                  value={maxRate}
                  onChange={(e) => setMaxRate(e.target.value)}
                  placeholder="3.00"
                  className="w-full pl-3 pr-8 py-2 text-sm text-stone-800 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                />
                <Percent size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400" />
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-stone-700 mb-1">Max Flat fee ($)</label>
              <div className="relative">
                <DollarSign size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <input
                  type="text"
                  inputMode="numeric"
                  value={maxFlatDisplay}
                  onChange={handleMaxFlatChange}
                  placeholder="0.30"
                  className="w-full pl-7 pr-3 py-2 text-sm text-stone-800 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                />
              </div>
              <p className="text-[10px] text-stone-500 mt-1">Type as cents — e.g. <span className="font-mono">30</span> → $0.30, <span className="font-mono">100</span> → $1.00</p>
            </div>
          </div>
        </div>

        {/* Optional metadata */}
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div>
            <label className="block text-[11px] font-medium text-stone-600 mb-1">Version (optional)</label>
            <input
              type="text"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="v1, 2026.01"
              className="w-full px-3 py-2 text-sm text-stone-800 border border-stone-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-stone-600 mb-1">Effective Date (optional)</label>
            <input
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              className="w-full px-3 py-2 text-sm text-stone-800 border border-stone-300 rounded-lg"
            />
          </div>
        </div>

        <div className="mt-3">
          <label className="block text-[11px] font-medium text-stone-600 mb-1">Notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 text-sm text-stone-800 border border-stone-300 rounded-lg resize-none"
          />
        </div>

        <label className="flex items-center gap-2 mt-4 cursor-pointer">
          <input type="checkbox" checked={setActive} onChange={(e) => setSetActive(e.target.checked)} className="w-4 h-4 accent-emerald-600" />
          <span className="text-sm text-stone-700">Set as active agreement (seeds runtime ceilings)</span>
        </label>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-stone-600 hover:text-stone-800">Cancel</button>
          <button
            onClick={handleUpload}
            disabled={uploading}
            className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {uploading ? <RubyGemMark size={14} drawing color="currentColor" /> : <Upload size={14} />}
            Create &amp; Activate
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgreementUploadModal;
